import * as React from "react";

import StatisticsCard from "lib/core_components/StatisticsCard";
import { removeTrailingSlash } from "lib/utils";
import { EventData, EventNames, useTracking } from "../../lib/tracking";
import { PrimaryTab, SecondaryTab } from "./supplier_utils";

import css from "./SupplierHeaderCards.module.scss";

type Props = {
  awardsCount: number;
  mentionsCount: number;
  expiriesCount: number;
  keyAccountsCount: number;
  isLoading: boolean;
  /** Current tab path relative to /suppliers/$id (e.g. "/awards"). Used to highlight cards. */
  currentTabPath: string;
  handleNavigation: (
    location: PrimaryTab | SecondaryTab,
    eventName: EventNames,
    data: EventData,
  ) => void;
};

function SupplierHeaderCards({
  awardsCount,
  mentionsCount,
  expiriesCount,
  keyAccountsCount,
  isLoading,
  currentTabPath,
  handleNavigation,
}: Props): JSX.Element {
  const { trackingData } = useTracking();
  // Match `getTabConfigFromLocation`'s trailing-slash strip so `/awards/` highlights too.
  const normalizedTabPath = removeTrailingSlash(currentTabPath);
  return (
    <div className={css.container}>
      <StatisticsCard
        value={awardsCount}
        loading={isLoading}
        objectType="Direct award"
        description={`Connected to this supplier`}
        onClick={() =>
          handleNavigation(SecondaryTab.AWARDS, EventNames.highlightClicked, {
            "Highlight type": "Direct awards connected to this supplier",
            "Context source": "Header card",
            ...trackingData,
          })
        }
        active={normalizedTabPath === SecondaryTab.AWARDS}
      />
      <StatisticsCard
        value={mentionsCount}
        loading={isLoading}
        objectType="Notice mention"
        description={`Connected to this supplier`}
        onClick={() =>
          handleNavigation(SecondaryTab.MENTIONS, EventNames.highlightClicked, {
            "Highlight type": "Notice mentions connected to this supplier",
            "Context source": "Header card",
            ...trackingData,
          })
        }
        active={normalizedTabPath === SecondaryTab.MENTIONS}
      />
      <StatisticsCard
        value={expiriesCount}
        loading={isLoading}
        objectType="Upcoming expiry"
        pluralObjectType="Upcoming expiries"
        description={"Connected to this supplier"}
        onClick={() =>
          handleNavigation(SecondaryTab.UPCOMING_EXPIRIES, EventNames.highlightClicked, {
            "Highlight type": "Upcoming expiries matching signals",
            "Context source": "Header card",
            ...trackingData,
          })
        }
        active={normalizedTabPath === SecondaryTab.UPCOMING_EXPIRIES}
      />
      <StatisticsCard
        value={keyAccountsCount}
        loading={isLoading}
        objectType="Saved buyer"
        description={`Connected to this supplier`}
        onClick={() =>
          handleNavigation(SecondaryTab.SAVED_BUYERS, EventNames.highlightClicked, {
            "Highlight type": "Saved buyers connected to this supplier",
            "Context source": "Header card",
            ...trackingData,
          })
        }
        active={normalizedTabPath === SecondaryTab.SAVED_BUYERS}
      />
    </div>
  );
}

export default SupplierHeaderCards;
